import axios from 'axios';
import moment from 'moment';
import downloadFile from 'downloadjs';
import { has, isNil, isObject, omit, pick } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { updateOutfit } from 'modules/subcampaigns/reducers/offerDeliverables';
import { updateCampaignDeliverableOutfit } from 'modules/campaigns/reducers/campaignDeliverables';

import { createReducer } from 'utils/redux';
import { API, destroy, get, patch, post, put } from 'utils/api';
import {
  UserException,
  downloadIndividualFile,
  downloadIndividualFileWithProxying,
  waitUntilZipIsReadyAndGetPresignedURL
} from 'utils/file';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import {
  PARTNER_GET_THE_LOOK,
  REALM_OUTFIT_DOWNLOAD_STATUS_FALLBACK,
  REALM_OUTFIT_STATUS_DECLINED,
  isRecourceInfoValid
} from 'utils/realm-outfits';
import { Querystring } from 'utils/url';
import { uploadFile } from 'utils/upload-asset';

applyTranslations(require('../components/realm-outfit-review/realm-outfit-review.i18n.json'));

import type {
  Action,
  Pagination,
  RealmOutfit,
  RealmOutfitDeclinedReason,
  RealmOutfitForm,
  RealmOutfitImageProps,
  RealmOutfitItem,
  RealmOutfitItemResource,
  RealmOutfitRawVideo,
  RealmOutfitVideo,
  RealmOutfitsQuery,
  ThunkAction
} from 'types';
import { RealmOutfitAudioType } from '../types/RealmOutfit.type';

export type PAGE_TYPE = 'offer' | 'content';

const LOADING = 'realmOutfits/LOADING';
const LOADED = 'realmOutfits/LOADED';
const RESET = 'realmOutfits/RESET';
const PUBLISHING = 'realmOutfits/PUBLISHING';
const UNPUBLISHING = 'realmOutfits/UNPUBLISHING';
const OUTFIT_UPDATED = 'realmOutfits/OUTFIT_UPDATED';
const OUTFIT_RESOURCE_ADDED = 'realmOutfits/OUTFIT_RESOURCE_ADDED';
const OUTFIT_RESOURCE_REMOVED = 'realmOutfits/OUTFIT_RESOURCE_REMOVED';
const REVIEW_STATUS_CHANGED = 'realmOutfits/REVIEW_STATUS_CHANGED';
const ASSIGN_VIDEO_TO_OUTFIT = 'realmOutfits/ASSIGN_VIDEO_TO_OUTFIT';

const ITEMS_PER_PAGE = 50;

const ALLOWED_FIELDS = ['name', 'captions', 'tags', 'genders', 'publishedCountries', 'audioType'];

type State = {
  isLoading: boolean,
  isPublishing: boolean,
  list: Array<RealmOutfit>,
  pagination: Pagination,
  isUnpublishing: boolean
};

const initialState: State = {
  isLoading: false,
  isPublishing: false,
  list: [],
  pagination: {},
  isUnpublishing: false
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

function setPublishingStatus(status: boolean): Action {
  return {
    type: PUBLISHING,
    payload: {
      status
    }
  };
}

function setUnpublishingStatus(status: boolean): Action {
  return {
    type: UNPUBLISHING,
    payload: {
      status
    }
  };
}

export function resetRealmOutfits(): Action {
  return {
    type: RESET
  };
}

function realmOutfitsLoaded(data): Action {
  return {
    type: LOADED,
    payload: {
      data
    }
  };
}

function reviewStatusChanged(outfitId, status, declinedReason?: RealmOutfitDeclinedReason): Action {
  return {
    type: REVIEW_STATUS_CHANGED,
    payload: {
      outfitId,
      status,
      declinedReason
    }
  };
}

function outfitUpdated(outfitId: number, outfit: RealmOutfit) {
  return {
    type: OUTFIT_UPDATED,
    payload: {
      outfitId,
      updatedOutfit: outfit
    }
  };
}

function outfitResourceAdded(outfitId: number, item: RealmOutfitItem) {
  return {
    type: OUTFIT_RESOURCE_ADDED,
    payload: {
      outfitId,
      addedItem: item
    }
  };
}

function outfitResourceRemoved(outfitId: number, itemId: number) {
  return {
    type: OUTFIT_RESOURCE_REMOVED,
    payload: {
      outfitId,
      itemId
    }
  };
}

function assignVideoToOutfit(
  outfitId: number,
  video: null | RealmOutfitVideo
): Action {
  return {
    type: ASSIGN_VIDEO_TO_OUTFIT,
    payload: {
      outfitId,
      video
    }
  };
}

const getErrorMessageByCode = (
  file: File | undefined,
  errorCode: string | undefined,
  errorDetail: string | undefined
): string | null => {
  // extract video duration and max duration from BE response
  const durationDetails: Array<string> = (errorDetail || '').match(/(\d+.s?)+/gi) || [];

  const errorMsg = __t(`realm_outfit_review.errors.${errorCode}`, {
    actualLength: durationDetails[0] || '',
    maxLength: durationDetails[1] || '',
    fileSize: file?.size || '',
    contentType: file?.type || ''
  });

  return errorMsg;
};

export const getCommonErrorMessage = (
  error: any
): string => {
  const errorStatus = error.response?.data?.status;
  let errorMsg = __t('realm_outfit_review.message_failed');

  if (errorStatus === 403) {
    errorMsg = __t('realm_outfit_review.errors.not_authorized');
  }

  if (errorStatus === 424) {
    errorMsg = __t('realm_outfit_review.errors.dependent_service');
  }

  if (errorStatus === 500) {
    errorMsg = __t('realm_outfit_review.errors.server_error');
  }

  return errorMsg;
};

export function enableOutfitSection(
  influencerId: number,
  emInfluencerUuid: string,
  isSCP: boolean
) {
  return async (dispatch) => {
    try {
      if (isSCP) {
        await put(API.END_POINTS.ADMIN.REALM.matchOutfitCreators(influencerId, emInfluencerUuid));
      } else {
        await put(API.END_POINTS.ADMIN.REALM.getEnableOutfitSectionLink(influencerId), {
          data: { isRealm: true, emInfluencerUuid }
        });
      }
    } catch (error) {
      dispatch(danger(__t('profile_outfit_panel.outfit_section_error_message')));
      throw error;
    }
  };
}

export function removeEmsMapping(influencerId: number): ThunkAction {
  return async dispatch => {
    try {
      await destroy(API.END_POINTS.ADMIN.REALM.matchOutfitCreators(influencerId));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('profile_outfit_panel.outfit_section_error_message')));
    }
  };
}

export function disableOutfitSection(influencerId: number) {
  return async (dispatch) => {
    try {
      await put(API.END_POINTS.ADMIN.REALM.getEnableOutfitSectionLink(influencerId), {
        data: { isRealm: false }
      });
    } catch (error) {
      dispatch(danger(__t('profile_outfit_panel.outfit_section_error_message')));
      throw error;
    }
  };
}

export function addRealmOutfitResource(outfitId: number, item: RealmOutfitItemResource, deliverableId?: number): ThunkAction {
  return async (dispatch) => {
    try {
      const url = deliverableId ? API.END_POINTS.REALM_OUTFITS.addOutfitItem(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.addOutfitItem(outfitId);

      const addedResourceResponse = await post(url, {
        data: item
      });

      dispatch(outfitResourceAdded(outfitId, addedResourceResponse.data));
    } catch (error) {
      dispatch(danger(__t('realm_outfit_review.update_outfit_error_message')));
      throw error;
    }
  };
}

export function removeRealmOutfitResource(outfitId: number, itemId: number, deliverableId?: number): ThunkAction {
  return async (dispatch) => {
    try {
      const url = deliverableId ? API.END_POINTS.REALM_OUTFITS.deleteOutfitItem(outfitId, itemId, deliverableId) : API.END_POINTS.ADMIN.REALM.deleteOutfitItem(outfitId, itemId);

      await destroy(url);
      dispatch(outfitResourceRemoved(outfitId, itemId));
    } catch (error) {
      dispatch(danger(__t('realm_outfit_review.update_outfit_error_message')));
      throw error;
    }
  };
}

let fetchRealmOutfitsCancelToken;

async function updateAudioType(
  outfitId: number,
  deliverableId: number,
  videoId: number,
  audioType: RealmOutfitAudioType | null
): Promise<any> {
  return put(
    API.END_POINTS.REALM_OUTFITS.outfitAudioType(outfitId, deliverableId, videoId),
    { data: { audio_type: audioType } }
  );
}

export const handleVideoUpload = (
  video: null | RealmOutfitRawVideo,
  shouldUpdateAudioType: boolean,
  outfitId: number,
  deliverableId?: number
) => async (dispatch) => {
  let videoUrl: string | null = null;

  if (!isNil(video)) {
    const uploadUrl = API.END_POINTS.ADMIN.REALM.uploadOutfitVideo();
    const uploadResponse = await uploadFile(video.file, uploadUrl);

    if (uploadResponse.type === 'success' && uploadResponse.data) {
      videoUrl = uploadResponse.data.data.url;
    }

    // the following `throw` covers both 'unsupported type' and 'file size (in bytes)' errors
    // the video duration error is triggered from the `saveResponse` PUT below this section
    if (uploadResponse.type === 'error') {
      throw new UserException(
        uploadResponse.error,
        uploadResponse.file
      );
    }
  }

  const outfitVideosUrl = deliverableId ?
    API.END_POINTS.REALM_OUTFITS.outfitVideos(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.outfitVideos(outfitId);
  const saveResponse = await put(outfitVideosUrl, { data: { url: videoUrl } });
  let newVideo = saveResponse?.data ?? null;

  if (!isNil(deliverableId) && !isNil(newVideo) && shouldUpdateAudioType) {
    const audioTypeResponse = await updateAudioType(outfitId, deliverableId, newVideo.id, video?.audioType ?? null);
    newVideo = audioTypeResponse?.data ?? null;
  }

  dispatch(assignVideoToOutfit(outfitId, newVideo || null));
};

const isRealmOutfitRawVideo = (video: any): video is RealmOutfitRawVideo => (
  isObject(video) && has(video, 'file')
);

export function updateRealmOutfit(
  updatedOutfit: RealmOutfitForm,
  deliverableId?: number,
  offerId?: number,
  pageType?: PAGE_TYPE): ThunkAction {
  return async (dispatch, getState) => {
    const loadingUrl = 'realm-outfit-details-update';

    try {
      const outfitId = updatedOutfit.id;
      const video = updatedOutfit.video;
      const audioType = video?.audioType ?? null;
      const realmOutfitAudioTypeEnabled = getState().runtimeConfig.config.realmOutfitAudioTypeEnabled ?? false;
      const shouldUpdateAudioType = realmOutfitAudioTypeEnabled && updatedOutfit.initialAudioType !== audioType;
      const saveOutfitInfoUrl =
        deliverableId ? API.END_POINTS.REALM_OUTFITS.saveOutfitInfo(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.saveOutfitInfo(outfitId);
      const hasPublishedTimeframe = updatedOutfit.publishedFrom && updatedOutfit.publishedUntil;
      const timeframe = hasPublishedTimeframe ? {
        publishedFrom: updatedOutfit.publishedFrom,
        publishedUntil: updatedOutfit.publishedUntil
      } : {};

      dispatch(showLoading(loadingUrl));

      const response = await put(saveOutfitInfoUrl, {
        data: {
          ...pick(updatedOutfit, ALLOWED_FIELDS),
          ...timeframe,
          businessPartner: PARTNER_GET_THE_LOOK,
          images: updatedOutfit.images.map(({ url, name }, index) => ({
            url,
            sortOrder: index,
            name
          }))
        }
      });

      dispatch(outfitUpdated(outfitId, response.data));

      if (isRealmOutfitRawVideo(video) || video === null) {
        await dispatch(handleVideoUpload(video, shouldUpdateAudioType, outfitId, deliverableId));
      }

      if (!isNil(deliverableId) && !isNil(video) && !isRealmOutfitRawVideo(video) && shouldUpdateAudioType) {
        const audioTypeResponse = await updateAudioType(outfitId, deliverableId, video.id, audioType);
        const newVideo = audioTypeResponse?.data ?? null;
        dispatch(assignVideoToOutfit(outfitId, newVideo || null));
      }

      await Promise.all(updatedOutfit.deletedItemsIds.map((itemId) => (
        dispatch(removeRealmOutfitResource(outfitId, itemId, deliverableId))
      )));

      await Promise.all(updatedOutfit.addeditems.map(item => (
        dispatch(addRealmOutfitResource(outfitId, item, deliverableId))
      )));

      if (deliverableId && offerId) {
        const updatedOutfitResponse = await get(API.END_POINTS.REALM_OUTFITS.getOutfit(outfitId, deliverableId));

        if (pageType === 'offer') {
          dispatch(updateOutfit(offerId, deliverableId, outfitId, updatedOutfitResponse.data));
        } else {
          dispatch(updateCampaignDeliverableOutfit(deliverableId, outfitId, updatedOutfitResponse.data));
        }
      }

      dispatch(success(__t('realm_outfit_review.message_save')));
    } catch (errorDetails) {
      // standardErrorObj: created when PUTing the outfit or PUTing the video url
      // customErrorObj: created when uploading the file (see `uploadFile` in `upload-asset.ts`)
      const standardErrorObj = errorDetails.response;
      const customErrorObj = isNil(standardErrorObj) ? errorDetails : undefined;
      const genericErrorMsg = __t('realm_outfit_review.update_outfit_error_message');
      const throwableError = customErrorObj?.error || errorDetails;

      if (isNil(standardErrorObj) && !customErrorObj.error) {
        dispatch(danger(__t('realm_outfit_review.message_failed')));

        return;
      }

      const errorData = !isNil(standardErrorObj)
        ? standardErrorObj.data
        : customErrorObj.error.response?.data;

      const errorDetail = errorData?.detail;
      const errorMsgByCode = getErrorMessageByCode(errorDetails.file, errorData?.code, errorDetail);
      let errorMsg = errorMsgByCode || genericErrorMsg;

      if (errorDetail?.includes('Publish dates are invalid')) {
        errorMsg = __t('realm_outfit_review.errors.published_dates_invalid');
      } else {
        errorMsg = errorDetail;
      }

      dispatch(danger(errorMsg));
      logError(throwableError);

      throw throwableError;
    } finally {
      dispatch(hideLoading(loadingUrl));
    }
  };
}

export const MIN_WIDTH = 1208;
export const MIN_HEIGHT = 1744;
export const MAX_WIDTH = 5000;
export const MAX_SIZE = 20971520;
export const DIMENSION_RATIO = MIN_WIDTH / MIN_HEIGHT;
export const DIMENSION_RATIO_DEVIATION = 0.006;

type ValidationRestrictions = {
  minHeight: number,
  minWidth: number,
  maxWidth: number,
  maxRatio: number,
  maxRatioDeviation: number,
  maxSize: number
};

export const getImageValidationError = (
  imageProps: RealmOutfitImageProps,
  restrictions: ValidationRestrictions
): string | null => {
  if (imageProps.width < restrictions.minWidth || imageProps.height < restrictions.minHeight) {
    return 'dimension';
  }

  if (imageProps.width > restrictions.maxWidth) {
    return 'max_width';
  }

  if (imageProps.size > restrictions.maxSize) {
    return 'size';
  }

  if (
    imageProps.ratio < restrictions.maxRatio - restrictions.maxRatioDeviation ||
    imageProps.ratio > restrictions.maxRatio + restrictions.maxRatioDeviation
  ) {
    return 'dimension_ratio';
  }

  return null;
};

export const downloadFileIndividually = (url: string): ThunkAction => (
  async (dispatch) => {
    const loadingId = `download-image-${Math.random()}`;
    const fileName = url.split('/').pop();

    dispatch(showLoading(loadingId));

    try {
      await downloadIndividualFileWithProxying(url, fileName || 'attachment');
    } catch (error) {
      logError(error);
    }

    dispatch(hideLoading(loadingId));
  }
);

export function downloadImages(
  outfitId: number,
  deliverableId?: number | null
): ThunkAction {
  return async (dispatch) => {
    const loadingId = `outfit-${outfitId}`;
    const apiURL = deliverableId ? API.END_POINTS.REALM_OUTFITS.downloadOutfitImages(outfitId, `outfit_${outfitId}`, deliverableId)
      : API.END_POINTS.ADMIN.REALM.downloadOutfitImages(outfitId, `outfit_${outfitId}`);

    dispatch(showLoading(loadingId));

    try {
      const response = await get(apiURL);
      const zipFileS3Url = response?.data?.url;
      const jsonFileS3Url = response?.data?.statusUrl;

      if (zipFileS3Url && jsonFileS3Url) {
        // wait a bit before trying to download zip otherwise it's possible to get 403 from S3
        // probably because it's accessed too soon after file creation/update
        // eslint-disable-next-line no-promise-executor-return
        await new Promise(resolve => setTimeout(resolve, 1000));

        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Zippy returned an empty response');
      }
      dispatch(hideLoading(loadingId));
    } catch (error) {
      dispatch(danger(__t('realm_outfit_review.loading_zip_error')));
      dispatch(hideLoading(loadingId));
    }
  };
}

export function downloadOutfitsExcel(statuses: Array<string>): ThunkAction {
  const statusesToDownload = statuses.length === 0
    ? REALM_OUTFIT_DOWNLOAD_STATUS_FALLBACK
    : statuses;
  const url = API.END_POINTS.ADMIN.REALM.exportOutfits(statusesToDownload.join(','));

  const date = moment().format('YYYY-MM-DD');
  const fileName = `Collabary Realm Outfits ${date}.xlsx`;

  return async (dispatch) => {
    dispatch(showLoading(url));
    try {
      const { data } = await get(url, { responseType: 'blob' }, { suppressGlobalNotification: true });
      downloadFile(data, fileName);
    } catch (err) {
      logError(err);
      dispatch(danger(__t('realm_outfit_review.loading_spreadsheet_error')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function fetchRealmOutfits(query: RealmOutfitsQuery): ThunkAction {
  const queryString = Querystring.objectToString({
    itemsPerPage: ITEMS_PER_PAGE,
    page: query.page || 1,
    search: query.search || '',
    sort: query.sort,
    influencer_id: query.influencerId || '',
    statuses: query.status
  });

  const url = `${API.END_POINTS.ADMIN.REALM.getOutfits()}?${queryString}`;

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    if (fetchRealmOutfitsCancelToken) {
      fetchRealmOutfitsCancelToken.cancel();
    }

    fetchRealmOutfitsCancelToken = axios.CancelToken.source();

    try {
      const response = await get(url, {
        cancelToken: fetchRealmOutfitsCancelToken.token
      }, {
        suppressGlobalNotification: true
      });

      dispatch(realmOutfitsLoaded(response.data));
    } catch (error) {
      logError(error);

      dispatch(resetRealmOutfits());
      if (!axios.isCancel(error)) {
        dispatch(setLoadingStatus(false));
      }
    }
  };
}

export function publishRealmOutfit(
  outfitId: number,
  deliverableId?: number,
  offerId?: number,
  pageType?: PAGE_TYPE): ThunkAction {
  const url = deliverableId ?
    API.END_POINTS.REALM_OUTFITS.publishOutfit(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.publishOutfit(outfitId);

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));
      dispatch(setPublishingStatus(true));

      const response = await put(url, {}, { suppressGlobalNotification: true });
      const updatedOutfit = response.data;
      dispatch(outfitUpdated(outfitId, updatedOutfit));

      if (offerId && deliverableId) {
        if (pageType === 'offer') {
          dispatch(updateOutfit(offerId, deliverableId, outfitId, updatedOutfit));
        } else {
          dispatch(updateCampaignDeliverableOutfit(deliverableId, outfitId, updatedOutfit));
        }
      }

      if (updatedOutfit.publishedError) {
        throw new Error(updatedOutfit.publishedError);
      }

      dispatch(setPublishingStatus(false));
      dispatch(success(__t('realm_outfit_review.message_publish')));
    } catch (error) {
      const errorMsg = getCommonErrorMessage(error);

      logError(error);

      dispatch(setPublishingStatus(false));
      if (error.response?.data?.code === 'outfits.no_ems_profile') {
        dispatch(danger(__t('realm_outfit_review.message_no_ems_profile')));
      } else {
        dispatch(danger(errorMsg));
      }
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function reTriggerVideoPipeline(deliverableId: number, outfitId: number): ThunkAction {
  const url = API.END_POINTS.REALM_OUTFITS.reTriggerVideoPipeline(deliverableId, outfitId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      await patch(url, {}, { suppressGlobalNotification: true });
    } catch (error) {
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function unPublishRealmOutfit(
  outfitId: number,
  deliverableId?: number,
  offerId?: number,
  pageType?: PAGE_TYPE): ThunkAction {
  const url = deliverableId ?
    API.END_POINTS.REALM_OUTFITS.unpublishOutfit(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.unpublishOutfit(outfitId);

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));
      dispatch(setUnpublishingStatus(true));

      const response = await put(url, {}, { suppressGlobalNotification: true });
      const updatedOutfit = response.data;
      dispatch(outfitUpdated(outfitId, updatedOutfit));

      if (offerId && deliverableId) {
        if (pageType === 'offer') {
          dispatch(updateOutfit(offerId, deliverableId, outfitId, updatedOutfit));
        } else {
          dispatch(updateCampaignDeliverableOutfit(deliverableId, outfitId, updatedOutfit));
        }
      }

      dispatch(setUnpublishingStatus(false));
      dispatch(success(__t('realm_outfit_review.message_unpublish')));
    } catch (error) {
      const errorMsg = getCommonErrorMessage(error);

      logError(error);

      dispatch(setUnpublishingStatus(false));
      dispatch(danger(errorMsg));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function declineRealmOutfit(
  outfitId: number,
  declinedReason: string,
  deliverableId?: number,
  offerId?: number,
  pageType?: PAGE_TYPE): ThunkAction {
  const url = deliverableId ?
    API.END_POINTS.REALM_OUTFITS.declineOutfit(outfitId, deliverableId) : API.END_POINTS.ADMIN.REALM.declineOutfit(outfitId);

  return async (dispatch) => {
    try {
      const response = await put(url, { data: { declinedReason } }, {
        suppressGlobalNotification: true
      });

      dispatch(reviewStatusChanged(outfitId, REALM_OUTFIT_STATUS_DECLINED, {
        text: declinedReason,
        date: moment().format()
      }));

      if (deliverableId && offerId) {
        if (pageType === 'offer') {
          dispatch(updateOutfit(offerId, deliverableId, outfitId, response.data));
        } else {
          dispatch(updateCampaignDeliverableOutfit(deliverableId, outfitId, response.data));
        }
      }

      dispatch(success(__t('realm_outfit_review.message_declined')));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('realm_outfit_review.message_failed')));
    }
  };
}

let fetchOutfitItemCancelToken;

export const fetchItemInfoByURLOrSKU = (urlOrSKU: string) => () => {
  const url = API.END_POINTS.ADMIN.REALM.checkOutfitSKU(urlOrSKU);

  if (fetchOutfitItemCancelToken) {
    fetchOutfitItemCancelToken.cancel();
  }

  fetchOutfitItemCancelToken = axios.CancelToken.source();

  if (urlOrSKU.length === 0) {
    return Promise.resolve();
  }

  return get(url, {
    cancelToken: fetchOutfitItemCancelToken.token
  }, {
    suppressGlobalNotification: true
  }).then(response => {
    const resource = response.data;

    if (!isRecourceInfoValid(resource)) {
      throw new Error(__t('admin_realm_outfit_item_form.invalid_article_input'));
    }

    return resource;
  });
};

export default createReducer<State, any>('realmOutfits', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [PUBLISHING]: (state, { status }) => ({
    ...state,
    isPublishing: status
  }),
  [UNPUBLISHING]: (state, { status }) => ({
    ...state,
    isUnpublishing: status
  }),
  [LOADED]: (state, { data }) => ({
    ...state,
    list: data.data,
    pagination: {
      ...data.meta,
      links: data.links
    },
    isLoading: false
  }),
  [OUTFIT_UPDATED]: (state, { outfitId, updatedOutfit }) => ({
    ...state,
    list: (state.list || []).map(outfit => {
      if (outfit.id === outfitId) {
        return {
          ...outfit,
          ...omit(updatedOutfit, ['items'])
        };
      }

      return outfit;
    })
  }),
  [OUTFIT_RESOURCE_ADDED]: (state, { outfitId, addedItem }) => ({
    ...state,
    list: (state.list || []).map(outfit => {
      if (outfit.id === outfitId) {
        return {
          ...outfit,
          items: [
            ...outfit.items.filter(item => item.id !== addedItem.id),
            addedItem
          ]
        };
      }

      return outfit;
    })
  }),
  [OUTFIT_RESOURCE_REMOVED]: (state, { outfitId, itemId }) => ({
    ...state,
    list: (state.list || []).map(outfit => {
      if (outfit.id === outfitId) {
        return {
          ...outfit,
          items: [
            ...outfit.items.filter(item => item.id !== itemId)
          ]
        };
      }

      return outfit;
    })
  }),
  [REVIEW_STATUS_CHANGED]: (state, { outfitId, status, declinedReason }) => ({
    ...state,
    list: state.list.map(outfit => {
      if (outfit.id !== outfitId) {
        return outfit;
      }

      return {
        ...outfit,
        status,
        declineHistories: [
          declinedReason,
          ...outfit.declineHistories
        ].filter(Boolean)
      };
    })
  }),
  [ASSIGN_VIDEO_TO_OUTFIT]: (state, { outfitId, video }) => ({
    ...state,
    list: state.list.map(o => {
      if (o.id !== outfitId) {
        return o;
      }

      return {
        ...o,
        video
      };
    })
  })
});
